/**
 * Mock transport for the Alerts workspace.
 *
 * Enabled with `VITE_ALERTS_USE_MOCKS=true`; the scenario is picked with
 * `VITE_ALERTS_MOCK_SCENARIO`. Switching to the real API is env-only — no code
 * path in the workspace knows whether it is talking to mocks, because both go
 * through the same normalized page DTOs.
 *
 * Scenarios (each maps to an acceptance case in MIN-225):
 *   default      non-empty queue, 3 cursor pages, exact-timezone carryover,
 *                estimated ages present; non-empty history
 *   empty        empty queue and empty history
 *   fallback-tz  carryover count present but resolved in a fallback timezone
 *   unavailable  carryover count null with a reason
 *   truncated    history reports `truncated: true`
 *   failure      every read rejects with a typed API error
 */
import { AlertStatus } from "@/alerts/types/machine-alert.ts";
import { AlertApiError } from "@/alerts/api/envelope.ts";
import type {
  AlertHistoryFilterState,
  AlertHistoryPage,
  AlertQueueCarryover,
  AlertQueueFilterState,
  AlertQueuePage,
  AlertQueueRow,
  AlertRow,
} from "@/alerts/types/alerts-workspace.ts";

type MockScenario =
  | "default"
  | "empty"
  | "fallback-tz"
  | "unavailable"
  | "truncated"
  | "failure";

const env = import.meta.env as Record<string, string | undefined>;

export const isAlertsMockMode = (): boolean => env.VITE_ALERTS_USE_MOCKS === "true";

const scenario = (): MockScenario => (env.VITE_ALERTS_MOCK_SCENARIO as MockScenario) || "default";

/** Deterministic clock so fixtures read the same on every run. */
const T0 = Date.parse("2026-07-30T02:34:37Z");
const iso = (offsetMinutes: number): string => new Date(T0 + offsetMinutes * 60_000).toISOString();

const MACHINES = [
  { id: 12, name: "EH-740", mxid: "184430102141570F00" },
  { id: 13, name: "EH-741", mxid: "184430102141570F01" },
];

const TYPES = ["Boulder On Truck", "Dust Event", "Boulder Rehandling"];

const baseRow = (index: number, status: AlertStatus): AlertRow => {
  const machine = MACHINES[index % MACHINES.length];
  return {
    id: 400 + index,
    timestamp: iso(-index * 7),
    origin: "AI",
    type: TYPES[index % TYPES.length],
    event_uuid: `9e4336c0-7b6a-4096-8a8c-a52eb98cc${String(index).padStart(3, "0")}`,
    event_state: "dump",
    status,
    device_mxid: machine.mxid,
    machine_name: machine.name,
    location_name: "Sigma Lithium",
    location_timezone: "America/Sao_Paulo",
    image_count: 6,
    category: null,
    severity: index % 5 === 0 ? "high" : null,
    review_cycle: 0,
    activity_seq: 1,
    workflow_version: 1,
    pending_since: status === AlertStatus.Pending ? iso(-index * 7) : null,
    platform_received_at: iso(-index * 7 + 1),
    closed_at: status === AlertStatus.Pending ? null : iso(-index * 3),
    closed_by_label: status === AlertStatus.Pending ? null : "Darja",
    disposition: status === AlertStatus.Ignored ? "false_alert" : null,
    disposition_legacy_defaulted: false,
    latency: {
      predicted_at: null,
      created_at: null,
      create_live_published_at: null,
      model_inference_latency_ms: null,
      ingestion_latency_ms: null,
      live_publish_latency_ms: null,
      clock_skew_detected: false,
    },
    gps: null,
    preview: null,
    images: null,
  };
};

const queueRow = (index: number): AlertQueueRow => {
  const machine = MACHINES[index % MACHINES.length];
  return {
    ...baseRow(index, AlertStatus.Pending),
    machine_id: machine.id,
    waiting_since: iso(-index * 7 - 40),
    waiting_since_basis: index % 3 === 0 ? "platform_received_at" : "pending_since",
    // Every third row has an approximate age, so the "estimated" treatment is
    // always exercised alongside exact ones.
    waiting_since_estimated: index % 3 === 0,
    reopened: index % 8 === 0,
  };
};

const QUEUE_ROWS: AlertQueueRow[] = Array.from({ length: 23 }, (_, i) => queueRow(i));
const HISTORY_ROWS: AlertRow[] = Array.from({ length: 137 }, (_, i) =>
  baseRow(i, [AlertStatus.Pending, AlertStatus.Resolved, AlertStatus.Ignored][i % 3]),
);

const CARRYOVER: Record<string, AlertQueueCarryover> = {
  exact: {
    count: 131,
    estimated_count: 8,
    block_key: "2026-07-30:day",
    block_started_at: iso(-360),
    timezone: "America/Sao_Paulo",
    timezone_fallback: false,
    unavailable_reason: null,
  },
  fallback: {
    count: 131,
    estimated_count: 8,
    block_key: "2026-07-30:day",
    block_started_at: iso(-360),
    timezone: "UTC",
    timezone_fallback: true,
    unavailable_reason: null,
  },
  unavailable: {
    count: null,
    estimated_count: 0,
    block_key: "2026-07-30:day",
    block_started_at: iso(-360),
    timezone: "UTC",
    timezone_fallback: true,
    unavailable_reason: "Location timezone is not configured.",
  },
};

const carryoverFor = (s: MockScenario): AlertQueueCarryover => {
  if (s === "fallback-tz") return CARRYOVER.fallback;
  if (s === "unavailable") return CARRYOVER.unavailable;
  return CARRYOVER.exact;
};

/** Cursors are opaque to the app — here they are simply page indices. */
const CURSORS = ["p1", "p2", "p3"];
const PAGE_SIZE = 8;

const latency = () => new Promise((resolve) => setTimeout(resolve, 180));

export const mockAlertQueue = async (
  state: AlertQueueFilterState,
): Promise<AlertQueuePage> => {
  await latency();
  const s = scenario();
  if (s === "failure") {
    throw new AlertApiError("Mock queue failure.", 503);
  }

  const rows = s === "empty" ? [] : filterByType(QUEUE_ROWS, state.type_in);
  const pageIndex = Math.max(0, CURSORS.indexOf(state.cursor ?? CURSORS[0]));
  const start = pageIndex * PAGE_SIZE;
  const items = rows.slice(start, start + PAGE_SIZE);
  const hasNext = start + PAGE_SIZE < rows.length && pageIndex + 1 < CURSORS.length;

  return {
    items,
    // Deliberately larger than the loaded page: the UI must show the server
    // count, never `items.length`.
    count: rows.length === 0 ? 0 : rows.length + 133,
    asOf: iso(0),
    summary: {
      oldest_pending:
        rows.length === 0
          ? null
          : {
              alert_id: 271,
              waiting_since: iso(-820),
              waiting_since_basis: "platform_received_at",
              // Not on the loaded page — the UI must not derive this from `items`.
              estimated: true,
            },
      carryover: carryoverFor(s),
      by_source_machine: MACHINES.map((m, i) => ({
        machine_id: m.id,
        count: i === 0 ? 94 : 62,
        carryover_count: i === 0 ? 80 : 51,
        oldest_pending: null,
      })),
      by_alert_type: TYPES.map((type, i) => ({ type, count: [96, 41, 19][i] })),
      estimated_age_count: rows.length === 0 ? 0 : 11,
    },
    next: hasNext ? CURSORS[pageIndex + 1] : null,
    previous: pageIndex > 0 ? CURSORS[pageIndex - 1] : null,
  };
};

export const mockAlertHistory = async (
  state: AlertHistoryFilterState,
): Promise<AlertHistoryPage> => {
  await latency();
  const s = scenario();
  if (s === "failure") {
    throw new AlertApiError("Mock history failure.", 503);
  }

  const all =
    s === "empty"
      ? []
      : filterByType(HISTORY_ROWS, state.type_in).filter(
          (row) => !state.status_in?.length || state.status_in.includes(row.status),
        );
  const limit = state.limit ?? 50;
  const offset = state.offset ?? 0;
  const items = all.slice(offset, offset + limit);

  return {
    items,
    totalItems: all.length,
    itemsPerPage: limit,
    page: Math.floor(offset / limit) + 1,
    pageCount: Math.max(1, Math.ceil(all.length / limit)),
    truncated: s === "truncated",
  };
};

const filterByType = <T extends AlertRow>(rows: T[], typeIn?: string[]): T[] =>
  typeIn?.length ? rows.filter((row) => typeIn.includes(row.type)) : rows;
